// Client-side guard for custom-song uploads: size + magic-byte sniff (don't
// trust extension/content-type). This is a fast UX gate only — the analysis
// worker re-validates authoritatively, since the presigned PUT is bypassable.
export const MAX_SONG_BYTES = 60 * 1024 * 1024; // 60 MB

export interface IAudioValidationResult {
  ok: boolean;
  reason?: string;
}

const startsWith = (bytes: Uint8Array, sig: number[], offset = 0): boolean =>
  sig.every((b, i) => bytes[offset + i] === b);

const ascii = (s: string): number[] => Array.from(s, (c) => c.charCodeAt(0));

// Recognize the common audio containers by their leading bytes.
const isKnownAudio = (b: Uint8Array): boolean => {
  if (startsWith(b, ascii("ID3"))) return true; // mp3 with ID3 tag
  if (b[0] === 0xff && (b[1] & 0xe0) === 0xe0) return true; // mp3/aac (MPEG/ADTS frame sync)
  if (startsWith(b, ascii("RIFF")) && startsWith(b, ascii("WAVE"), 8))
    return true; // wav
  if (startsWith(b, ascii("ftyp"), 4)) return true; // m4a/aac (mp4 container)
  if (startsWith(b, ascii("OggS"))) return true; // ogg
  if (startsWith(b, ascii("fLaC"))) return true; // flac
  return false;
};

export const validateAudioFile = async (
  file: File,
): Promise<IAudioValidationResult> => {
  if (file.size === 0) return { ok: false, reason: "File is empty." };
  if (file.size > MAX_SONG_BYTES) {
    return {
      ok: false,
      reason: `File is too large (max ${MAX_SONG_BYTES / (1024 * 1024)} MB).`,
    };
  }
  const header = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  if (!isKnownAudio(header)) {
    return {
      ok: false,
      reason:
        "Unsupported file — use an mp3, wav, m4a/aac, ogg, or flac audio file.",
    };
  }
  return { ok: true };
};
